import React, { useState, useRef, useEffect } from 'react';
import {
  validateWoltSettlementFile,
  groupByVenue,
  WOLT_TYPE_LABELS,
} from '../utils/woltSettlementValidator';
import {
  getVenuesForWolt,
  getVenuesWithoutWoltName,
  updateVenueWoltName,
} from '../utils/woltVenueManager';
import { uploadWoltSettlementToVoucher } from '../utils/woltSettlementUpload';

const ACCEPT = '.xlsx,.xls,.csv';
const MAX_SIZE_MB = 10;
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;

const WoltPage = () => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [pivot, setPivot] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [venueByName, setVenueByName] = useState({});
  const [matchModalVenue, setMatchModalVenue] = useState(null);
  const [venuesWithoutWoltName, setVenuesWithoutWoltName] = useState([]);
  const [matchModalVenueId, setMatchModalVenueId] = useState('');
  const [matchModalLoading, setMatchModalLoading] = useState(false);
  const [matchModalError, setMatchModalError] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const inputRef = useRef(null);

  const allMatched =
    pivot?.rows?.length > 0 && pivot.rows.every((v) => venueByName[v]);

  useEffect(() => {
    if (!pivot?.rows?.length) return;
    let cancelled = false;
    getVenuesForWolt().then((res) => {
      if (cancelled || !res.success) return;
      const map = {};
      (res.venues || []).forEach((v) => {
        if (v.wolt_venue_name != null && String(v.wolt_venue_name).trim() !== '') {
          map[String(v.wolt_venue_name).trim()] = {
            id: v.id,
            department_name: v.department_name || v.wolt_venue_name || '',
          };
        }
      });
      setVenueByName(map);
    });
    return () => {
      cancelled = true;
    };
  }, [pivot]);

  const acceptFile = (f) => {
    if (!f) return;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/csv',
    ];
    const ext = (f.name || '').toLowerCase();
    const okType =
      validTypes.includes(f.type) ||
      /\.(xlsx|xls|csv)$/.test(ext) ||
      (f.type === '' && /\.(xlsx|xls|csv)$/.test(ext));
    if (!okType) {
      setValidation({
        isValid: false,
        errors: ['Please choose an Excel file (.xlsx, .xls) or CSV.'],
        warnings: [],
        data: [],
      });
      setPivot(null);
      return;
    }
    if (f.size > MAX_SIZE) {
      setValidation({
        isValid: false,
        errors: [`File is larger than ${MAX_SIZE_MB}MB.`],
        warnings: [],
        data: [],
      });
      setPivot(null);
      return;
    }
    setFile(f);
    setFileName(f.name);
    setValidation(null);
    setPivot(null);
    setUploadSuccess(null);
    setUploadError(null);
    setIsLoading(true);
    validateWoltSettlementFile(f)
      .then((result) => {
        setValidation(result);
        if (result.isValid && result.data?.length) {
          setPivot(groupByVenue(result.data));
        } else {
          setPivot(null);
        }
      })
      .catch((err) => {
        setValidation({
          isValid: false,
          errors: [err.message || 'Failed to read file.'],
          warnings: [],
          data: [],
        });
        setPivot(null);
      })
      .finally(() => setIsLoading(false));
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) {
      acceptFile(f);
    } else {
      setValidation({
        isValid: false,
        errors: ['No file received. Try selecting the file with the file picker instead.'],
        warnings: [],
        data: [],
      });
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const onInputChange = (e) => {
    const f = e.target?.files?.[0];
    if (f) acceptFile(f);
    e.target.value = '';
  };

  const formatAmount = (n) => {
    if (n == null || Number.isNaN(n)) return '–';
    return Number(n).toLocaleString('nb-NO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const openMatchModal = (venue) => {
    setMatchModalVenue(venue);
    setMatchModalVenueId('');
    setMatchModalError(null);
    getVenuesWithoutWoltName().then((res) => {
      if (res.success) setVenuesWithoutWoltName(res.venues || []);
      else setMatchModalError(res.error);
    });
  };

  const closeMatchModal = () => {
    setMatchModalVenue(null);
    setMatchModalVenueId('');
    setMatchModalError(null);
    setVenuesWithoutWoltName([]);
  };

  const handleUpload = async () => {
    if (!pivot || !validation?.data || !allMatched) return;
    setUploadLoading(true);
    setUploadError(null);
    setUploadSuccess(null);
    const res = await uploadWoltSettlementToVoucher({
      pivot,
      venueByName,
      settlementData: validation.data,
    });
    setUploadLoading(false);
    if (res.success) {
      setUploadSuccess(`Voucher uploads created (revenue: ${res.voucherUploadIdA}, bank: ${res.voucherUploadIdB}).`);
      setPivot(null);
      setValidation(null);
      setVenueByName({});
      setFile(null);
      setFileName('');
    } else {
      setUploadError(res.error || 'Upload failed');
    }
  };

  const saveMatch = async () => {
    if (!matchModalVenue || !matchModalVenueId) {
      setMatchModalError('Please select a department.');
      return;
    }
    setMatchModalLoading(true);
    setMatchModalError(null);
    const res = await updateVenueWoltName(matchModalVenueId, matchModalVenue);
    setMatchModalLoading(false);
    if (res.success) {
      const venue = venuesWithoutWoltName.find(
        (v) => String(v.id) === String(matchModalVenueId)
      );
      setVenueByName((prev) => ({
        ...prev,
        [matchModalVenue]: {
          id: venue?.id,
          department_name: venue?.department_name || venue?.wolt_venue_name || '',
        },
      }));
      setVenuesWithoutWoltName((prev) =>
        prev.filter((v) => String(v.id) !== String(matchModalVenueId))
      );
      closeMatchModal();
    } else {
      setMatchModalError(res.error || 'Failed to save');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-xl font-semibold text-black">
            Wolt settlement upload
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Upload a Wolt settlement Excel or CSV. Required columns:{' '}
            <span className="text-gray-600">Venue Name</span>,{' '}
            <span className="text-gray-600">Total Goods Sold</span>,{' '}
            <span className="text-gray-600">Total Gross Commission Amount</span>,{' '}
            <span className="text-gray-600">Total Payout Amount</span>.
          </p>
        </header>

        {uploadSuccess && (
          <div className="border border-gray-200 rounded-md p-6 mb-6 text-center">
            <p className="text-sm text-gray-600 mb-4">{uploadSuccess}</p>
            <button
              type="button"
              onClick={() => setUploadSuccess(null)}
              className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-900 transition-colors"
            >
              Upload another file
            </button>
          </div>
        )}

        {!uploadSuccess && (
          <div className="border border-gray-200 rounded-md p-5 mb-6">
            <label className="block text-xs font-medium text-gray-500 mb-2">File</label>
            <div
              role="button"
              tabIndex={0}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  inputRef.current?.click();
                }
              }}
              className={`
                border border-dashed rounded-md p-8 text-center cursor-pointer transition-colors
                ${dragOver ? 'border-gray-400 bg-gray-50' : 'border-gray-300 hover:border-gray-400'}
              `}
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                onChange={onInputChange}
                disabled={isLoading}
                className="hidden"
              />
              {isLoading ? (
                <p className="text-sm text-gray-500">Validating file...</p>
              ) : fileName ? (
                <p className="text-sm text-black font-medium">{fileName}</p>
              ) : (
                <p className="text-sm text-gray-400">
                  Click or drag and drop Excel (.xlsx, .xls) or CSV
                </p>
              )}
            </div>
          </div>
        )}

        {!uploadSuccess && validation && !validation.isValid && validation.errors?.length > 0 && (
          <div className="border border-red-200 rounded-md p-4 mb-6">
            <h3 className="text-xs font-semibold text-red-700 mb-1">Validation failed</h3>
            <ul className="list-disc list-inside text-sm text-red-600 space-y-0.5">
              {validation.errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {!uploadSuccess && validation && validation.warnings?.length > 0 && (
          <div className="border border-yellow-200 rounded-md p-4 mb-6">
            <h3 className="text-xs font-semibold text-yellow-700 mb-1">Warnings</h3>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-0.5">
              {validation.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {!uploadSuccess && validation?.isValid && validation.data?.length > 0 && (
          <p className="text-sm text-gray-600 mb-6">
            File is valid. {validation.data.length} rows. Grouped by venue below.
          </p>
        )}

        {!uploadSuccess && pivot && pivot.rows?.length > 0 && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-medium text-black">Summary by venue</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  Match all venues to enable Upload.
                </p>
              </div>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!allMatched || uploadLoading}
                className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-900 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {uploadLoading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
            {uploadError && (
              <div className="mx-5 mt-2 text-sm text-red-600">{uploadError}</div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[400px] text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-2.5 px-4 font-medium text-gray-500 sticky left-0 bg-gray-50 z-10">
                      Venue Name
                    </th>
                    <th className="text-left py-2.5 px-4 font-medium text-gray-500 bg-gray-50 z-10">
                      Department
                    </th>
                    {pivot.columns.map((col) => (
                      <th
                        key={col}
                        className="text-right py-2.5 px-4 font-medium text-gray-500 whitespace-nowrap"
                      >
                        {WOLT_TYPE_LABELS[col] || col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pivot.rows.map((venue) => (
                    <tr key={venue} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-4 font-medium text-black sticky left-0 bg-white z-10">
                        {venue}
                      </td>
                      <td className="py-2 px-4 text-gray-600">
                        {venueByName[venue]?.department_name ? (
                          venueByName[venue].department_name
                        ) : (
                          <button
                            type="button"
                            onClick={() => openMatchModal(venue)}
                            className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                          >
                            Match
                          </button>
                        )}
                      </td>
                      {pivot.columns.map((type) => (
                        <td key={type} className="py-2 px-4 text-right text-gray-700 tabular-nums">
                          {formatAmount(pivot.matrix.get(venue)?.get(type))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-medium">
                    <td className="py-2.5 px-4 text-black sticky left-0 bg-gray-50 z-10">Total</td>
                    <td className="py-2.5 px-4 bg-gray-50" />
                    {pivot.columns.map((type) => (
                      <td key={type} className="py-2.5 px-4 text-right text-black tabular-nums">
                        {formatAmount(pivot.colTotals.get(type))}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {matchModalVenue && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
            onClick={closeMatchModal}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="w-full max-w-md bg-white border border-gray-200 rounded-md shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-sm font-semibold text-black">
                  Match venue to department
                </h2>
                <p className="mt-1 text-xs text-gray-400">
                  Assign Wolt venue <span className="text-gray-600 font-medium">{matchModalVenue}</span> to a department.
                </p>
              </div>
              <div className="px-5 py-4">
                {matchModalError && (
                  <p className="mb-3 text-sm text-red-600">{matchModalError}</p>
                )}
                <label htmlFor="match-venue-select" className="block text-xs font-medium text-gray-500 mb-1.5">
                  Department
                </label>
                <select
                  id="match-venue-select"
                  value={matchModalVenueId}
                  onChange={(e) => setMatchModalVenueId(e.target.value)}
                  className="w-full border border-gray-200 rounded-md py-2 px-3 text-sm text-black focus:outline-none focus:border-black transition-colors"
                >
                  <option value="">Choose department</option>
                  {venuesWithoutWoltName.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.department_name || `Venue ${v.id}`}
                    </option>
                  ))}
                </select>
                {venuesWithoutWoltName.length === 0 && !matchModalError && (
                  <p className="mt-2 text-xs text-gray-400">
                    No departments without a Wolt venue name. Add a venue in Settings.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
                <button
                  type="button"
                  onClick={closeMatchModal}
                  className="px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveMatch}
                  disabled={matchModalLoading || !matchModalVenueId || venuesWithoutWoltName.length === 0}
                  className="px-3 py-2 text-sm bg-black text-white rounded-md hover:bg-gray-900 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                >
                  {matchModalLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WoltPage;
